import useLoginCheck from 'hooks/common/useLoginCheck';
import { createStore, getStore, updateStore } from 'lib/api/place';
import { changeProp, initialize } from 'modules/storeSlice';
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { validateStoreCreator } from 'utils/validate';

const usePlaceCreator = (id, setShowErrorModal, setNotiMsg) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isSubmit, setIsSubmit] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [errors, setErrors] = useState({});

  const { code, tel, space, addedSpace } = useSelector(
    ({ store }) => store.registerForm,
  );
  const newTel = tel.replace(/-/g, '');

  const getStoreQuery = useQuery(['store', id], () => getStore(id));
  useLoginCheck(getStoreQuery.error?.response, getStoreQuery.refetch);
  const store = getStoreQuery.data?.data;

  const createStoreMutation = useMutation(createStore, {
    onError: error => {
      if (error.response.data.error === 'AlreadyStoreExist') {
        setShowErrorModal(true);
        setNotiMsg(error.response.data.msg);
      }
    },
    onSuccess: () => {
      navigate('/admin/places');
      queryClient.invalidateQueries('dashboardCounts');
    },
  });
  useLoginCheck(createStoreMutation.error?.response, () => {
    createStoreMutation.mutate({
      storeId: store?.id,
      contractId: store?.contractId,
      code,
      tel: newTel,
      space,
    });
  });

  const updateStoreMutation = useMutation(updateStore, {
    onSuccess: () => {
      navigate('/admin/places');
    },
  });
  useLoginCheck(updateStoreMutation.error?.response, () => {
    updateStoreMutation.mutate({
      id,
      code,
      contractId: store?.contractId,
      tel: newTel,
      addedSpace,
    });
  });

  useEffect(() => {
    return () => {
      dispatch(initialize('registerForm'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (isSubmit && Object.keys(errors).length === 0) {
      createStoreMutation.mutate({
        storeId: store?.id,
        contractId: store?.contractId,
        code,
        tel: newTel,
        space,
      });
      setIsSubmit(false);
    }
  }, [
    isSubmit,
    errors,
    createStoreMutation,
    store?.id,
    store?.contractId,
    code,
    newTel,
    space,
  ]);

  useEffect(() => {
    if (isUpdate && Object.keys(errors).length === 0) {
      updateStoreMutation.mutate({
        id,
        code,
        contractId: store?.contractId,
        tel: newTel,
        addedSpace,
      });
      setIsUpdate(false);
    }
  }, [
    isUpdate,
    errors,
    updateStoreMutation,
    id,
    code,
    store?.contractId,
    newTel,
    addedSpace,
  ]);

  const onUpdate = () => {
    setErrors(validateStoreCreator(code, newTel));
    setIsUpdate(true);
  };

  const onSubmit = () => {
    setErrors(validateStoreCreator(code, newTel));
    setIsSubmit(true);
  };

  const onChange = e => {
    const { name, value } = e.target;
    dispatch(changeProp({ obj: 'registerForm', key: name, value }));
  };

  return {
    getStoreQuery,
    createStoreMutation,
    updateStoreMutation,
    store,
    code,
    tel,
    space,
    addedSpace,
    errors,
    onChange,
    onSubmit,
    onUpdate,
  };
};

export default usePlaceCreator;
